import { useEffect, useState } from 'react';

/**
 * Live countdown to a decision-window deadline.
 *
 * The server stamps `deadlineAt` on every decisionWindow it
 * publishes; this component just subtracts that from `Date.now()`
 * once a second and renders mm:ss. When the deadline passes the
 * label flips to "Time's up" — the worker process will resolve
 * the timeout server-side; the next SSE state push refreshes the
 * page.
 *
 * Style:
 *   - normal     : slate
 *   - <30 seconds: amber (your turn, get a move on)
 *   - <10 seconds: red, pulsing
 *
 * Pure presentation. No network. Safe to mount/unmount per render
 * because the underlying setInterval is keyed to the deadline.
 */
export function MatchClock({
  deadlineAt,
  className = '',
}: {
  deadlineAt: string | null | undefined;
  className?: string;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!deadlineAt) return;
    const tick = () => setNow(Date.now());
    tick();
    // Tick once per second. Hold a 1ms-resolution timer behind it
    // so the next minute boundary doesn't drift visibly.
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadlineAt]);

  if (!deadlineAt) return null;

  const remainingMs = new Date(deadlineAt).getTime() - now;
  if (Number.isNaN(remainingMs)) return null;

  if (remainingMs <= 0) {
    return (
      <span className={`font-display tabular-nums text-sm text-ec-oxblood-2 ${className}`} title={deadlineAt}>
        Time's up
      </span>
    );
  }

  const totalSeconds = Math.floor(remainingMs / 1000);
  // Scale the unit to the magnitude: days past 24h, hours past 60m, else m:ss.
  // (AFL/cricket default to a 24-hour turn timer, so "886:25" minutes is the
  // common case — show it as "14h 46m", not a runaway minute count.)
  let label: string;
  if (totalSeconds >= 86_400) {
    const days = Math.floor(totalSeconds / 86_400);
    const hours = Math.floor((totalSeconds % 86_400) / 3600);
    label = `${days}d ${hours}h`;
  } else if (totalSeconds >= 3600) {
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    label = `${hours}h ${String(mins).padStart(2, '0')}m`;
  } else {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    label = `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  // Only urgent colors below a minute. AFL/cricket default to a
  // 24-hour timer, so most matches will sit safely in slate.
  let color = 'text-ec-bone';
  let extra = '';
  if (totalSeconds < 10) {
    color = 'text-ec-oxblood-2';
    extra = ' animate-pulse';
  } else if (totalSeconds < 30) {
    color = 'text-ec-brass';
  }

  return (
    <span
      className={`font-display tabular-nums text-sm ${color}${extra} ${className}`}
      title={`Deadline ${deadlineAt}`}
    >
      {label}
    </span>
  );
}
